import type { ListVerificationsResponse, VerificationListItem } from "@wallyo/contracts/console/verifications";

/**
 * Oldest first, the way the server pages them: whoever has waited longest is
 * reviewed first. Ties keep a stable order by id, as the server's cursor does.
 */
export function oldestFirst(items: readonly VerificationListItem[]): VerificationListItem[] {
  return [...items].sort((a, b) => {
    const byTime = new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
    if (byTime !== 0) return byTime;
    return a.identityVerificationId < b.identityVerificationId ? -1 : 1;
  });
}

/** A document number that matches a banned one: shown apart, and never approvable. */
export const isFlagged = (item: Pick<VerificationListItem, "status" | "matchesBannedDocument">): boolean =>
  item.status === "flagged" || item.matchesBannedDocument;

/**
 * The queue as the screen holds it: the pages read so far. New filters replace
 * everything; "Charger plus" appends the next page; a failed "Charger plus"
 * keeps what is already shown.
 */
export type QueueState = {
  status: "loading" | "ready" | "error";
  items: readonly VerificationListItem[];
  nextCursor: string | null;
  error: unknown;
  loadingMore: boolean;
  moreError: unknown;
};

export type QueueEvent =
  | { type: "load" }
  | { type: "loaded"; page: ListVerificationsResponse }
  | { type: "failed"; error: unknown }
  | { type: "loadMore" }
  | { type: "loadedMore"; page: ListVerificationsResponse }
  | { type: "moreFailed"; error: unknown };

export const INITIAL_QUEUE_STATE: QueueState = {
  status: "loading",
  items: [],
  nextCursor: null,
  error: null,
  loadingMore: false,
  moreError: null,
};

export function queueReducer(state: QueueState, event: QueueEvent): QueueState {
  switch (event.type) {
    case "load":
      return INITIAL_QUEUE_STATE;
    case "loaded":
      return {
        ...INITIAL_QUEUE_STATE,
        status: "ready",
        items: oldestFirst(event.page.verifications),
        nextCursor: event.page.nextCursor,
      };
    case "failed":
      return { ...INITIAL_QUEUE_STATE, status: "error", error: event.error };
    case "loadMore":
      return { ...state, loadingMore: true, moreError: null };
    case "loadedMore": {
      // A page read twice (a retried click) adds nothing it already has.
      const seen = new Set(state.items.map((item) => item.identityVerificationId));
      const fresh = event.page.verifications.filter((item) => !seen.has(item.identityVerificationId));
      return {
        ...state,
        items: oldestFirst([...state.items, ...fresh]),
        nextCursor: event.page.nextCursor,
        loadingMore: false,
        moreError: null,
      };
    }
    case "moreFailed":
      return { ...state, loadingMore: false, moreError: event.error };
  }
}
